import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  WifiOff,
  Sync,
  GridView,
  ViewList,
  PersonAdd,
  People,
  PregnantWoman,
  ChildCare,
  PriorityHigh,
  Search,
  Category,
  Home,
  Sort,
  FilterList,
  Event,
  FamilyRestroom,
  Favorite,
  MoreVert,
  Elderly,
  FirstPage,
  ChevronLeft,
  ChevronRight,
  LastPage,
  AccountTree,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getBeneficiaryStats, getBeneficiariesList } from '../services/authService';
import axios from 'axios';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import { parseISO, isValid } from 'date-fns';

const BeneficiariesPage = () => {
  const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBeneficiaries: 0,
    pregnantWomen: 0,
    children: 0,
    highRiskCases: 0,
    elderly: 0,
  });
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    household: 'all',
    sort: 'name',
  });
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);

  const memoizedFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    const fetchHouseholds = async () => {
      try {
        const response = await axios.get('http://localhost:8000/api/asha/get-households');
        if (response.data.status) {
          setHouseholds(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching households:', error);
        toast.error('Failed to load households');
      }
    };
    fetchHouseholds();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getBeneficiaryStats();
        if (response.status) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        toast.error('Failed to load stats');
      }
    };
    fetchStats();

    const fetchBeneficiaries = async () => {
      setLoading(true);
      try {
        const response = await getBeneficiariesList({
          filters: {
            page: pagination.page,
            limit: pagination.limit,
            search: memoizedFilters.search,
            category: memoizedFilters.category,
            household: memoizedFilters.household,
            sort: memoizedFilters.sort,
          },
        });

        if (response.data.status) {
          setBeneficiaries(response.data.data || []);
          setPagination((prev) => ({
            ...prev,
            total: response.data.pagination.total || 0,
            totalPages: response.data.pagination.totalPages || 1,
          }));
        } else {
          throw new Error('Invalid response status');
        }
      } catch (error) {
        console.error('Error fetching beneficiaries:', error);
        toast.error('Failed to load beneficiaries');
      } finally {
        setLoading(false);
      }
    };

    fetchBeneficiaries();
  }, [pagination.page, pagination.limit, memoizedFilters, selectedVillageId]);

  const handleFamilyClick = (id) => {
    navigate(`/family-tree/${id}`);
  };

  const handleClick = () => {
    navigate('/beneficiary-add');
  };

  const handleViewToggle = (mode) => {
    setViewMode(mode);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page }));
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Pregnant Woman':
        return <PregnantWoman />;
      case 'Child':
        return <ChildCare />;
      case 'Elderly':
        return <Elderly />;
      default:
        return <People />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = parseISO(dateString);
    return isValid(date) ? date.toLocaleDateString() : 'Invalid Date';
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    const date = parseISO(dateString);
    return isValid(date) && date < new Date();
  };

  const calculateAge = (dobString) => {
    if (!dobString) return 'N/A';
    const birthDate = parseISO(dobString);
    if (!isValid(birthDate)) return 'N/A';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Get ANC visit count and latest next_visit_date for Pregnant Women
  const getAncInfo = (pregnancyTrackings) => {
    if (!pregnancyTrackings || pregnancyTrackings.length === 0) {
      return { ancVisitCount: 0, nextVisitDate: null };
    }
    const ancVisitCount = pregnancyTrackings.reduce((count, tracking) => {
      return count + (tracking.AncVisits?.length || 0);
    }, 0);
    const allNextVisitDates = pregnancyTrackings
      .flatMap((tracking) => tracking.AncVisits || [])
      .map((visit) => visit.next_visit_date)
      .filter((date) => date);
    const latestNextVisitDate = allNextVisitDates.length
      ? allNextVisitDates.sort((a, b) => new Date(b) - new Date(a))[0]
      : null;
    return { ancVisitCount, nextVisitDate: latestNextVisitDate };
  };

  const getPaginationButtons = () => {
    const maxButtons = 5;
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, pagination.page - half);
    let end = Math.min(pagination.totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  if (loading) return <Loader />;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Beneficiaries</div>
          <div className="page-subtitle">Manage and monitor your assigned beneficiaries</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="view-toggle">
            <div
              className={`view-option ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => handleViewToggle('grid')}
              role="button"
              aria-label="Grid View"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleViewToggle('grid')}
            >
              <GridView />
            </div>
            <div
              className={`view-option ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => handleViewToggle('list')}
              role="button"
              aria-label="List View"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleViewToggle('list')}
            >
              <ViewList />
            </div>
          </div>
          <button className="button" style={{ marginLeft: '16px' }} onClick={handleClick}>
            <PersonAdd className="icon" />
            Register New
          </button>
        </div>
      </div>

      <div className="quick-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#4CAF50' }}>
            <People />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.totalBeneficiaries}</div>
            <div className="stat-label">Total Beneficiaries</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#9C27B0' }}>
            <PregnantWoman />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.pregnantWomen}</div>
            <div className="stat-label">Pregnant Women</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#2196F3' }}>
            <ChildCare />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.children}</div>
            <div className="stat-label">Children (0-5 years)</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#F44336' }}>
            <PriorityHigh />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.highRiskCases}</div>
            <div className="stat-label">High Risk Cases</div>
          </div>
        </div>
      </div>

      <div className="search-bar">
        <div className="search-input">
          <Search className="icon" />
          <input
            type="text"
            placeholder="Search by name, ID, or phone number..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            aria-label="Search beneficiaries"
          />
        </div>
        <div className="filter-select">
          <Category className="icon" />
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            aria-label="Filter by category"
          >
            <option value="all">All Categories</option>
            <option value="Pregnant Woman">Pregnant Women</option>
            <option value="Child">Children</option>
            <option value="Elderly">Elderly</option>
            <option value="high_risk">High Risk</option>
          </select>
        </div>
        <div className="filter-select">
          <Home className="icon" />
          <select
            value={filters.household}
            onChange={(e) => handleFilterChange('household', e.target.value)}
            aria-label="Filter by household"
          >
            <option value="all">All Households</option>
            {households.map((household) => (
              <option key={household.id} value={household.id}>
                {household.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-select">
          <Sort className="icon" />
          <select
            value={filters.sort}
            onChange={(e) => handleFilterChange('sort', e.target.value)}
            aria-label="Sort by"
          >
            <option value="name">Sort by Name</option>
            <option value="date">Last Visit Date</option>
            <option value="risk">High Risk First</option>
            <option value="due">Due Services</option>
          </select>
        </div>
        <button className="button outline">
          <FilterList className="icon" />
          More Filters
        </button>
      </div>

      <div className="tabs">
        <div
          className={`tab ${filters.category === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('category', 'all')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleFilterChange('category', 'all')}
        >
          All Beneficiaries
          <span className="tab-indicator">{stats.totalBeneficiaries}</span>
        </div>
        <div
          className={`tab ${filters.category === 'Pregnant Woman' ? 'active' : ''}`}
          onClick={() => handleFilterChange('category', 'Pregnant Woman')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleFilterChange('category', 'Pregnant Woman')}
        >
          Pregnant Women
          <span className="tab-indicator">{stats.pregnantWomen}</span>
        </div>
        <div
          className={`tab ${filters.category === 'Child' ? 'active' : ''}`}
          onClick={() => handleFilterChange('category', 'Child')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleFilterChange('category', 'Child')}
        >
          Children
          <span className="tab-indicator">{stats.children}</span>
        </div>
        <div
          className={`tab ${filters.category === 'Elderly' ? 'active' : ''}`}
          onClick={() => handleFilterChange('category', 'Elderly')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleFilterChange('category', 'Elderly')}
        >
          Elderly
          <span className="tab-indicator">{stats.elderly}</span>
        </div>
        <div
          className={`tab ${filters.category === 'high_risk' ? 'active' : ''}`}
          onClick={() => handleFilterChange('category', 'high_risk')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleFilterChange('category', 'high_risk')}
        >
          High Risk
          <span className="tab-indicator">{stats.highRiskCases}</span>
        </div>
      </div>

      {viewMode === 'grid' && (
        <div className="beneficiary-cards">
          {beneficiaries.length > 0 ? (
            beneficiaries.map((beneficiary) => {
              const { ancVisitCount, nextVisitDate } = getAncInfo(beneficiary.PregnancyTrackings);
              return (
                <div className="beneficiarylist" key={beneficiary.beneficiary_id}>
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleFamilyClick(beneficiary.household_id)}
                    role="button"
                    aria-label={`View family tree for ${beneficiary.name}`}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleFamilyClick(beneficiary.household_id)}
                  >
                    <AccountTree fontSize="large" color="primary" />
                  </div>
                  <div className="card-header">
                    <div className={`b-avatar ${beneficiary.category}`}>
                      {getCategoryIcon(beneficiary.category)}
                    </div>
                    <div className="b-info">
                      <div className="b-name">{beneficiary.name || 'Unknown'}</div>
                      <div className="b-basic">
                        {calculateAge(beneficiary.date_of_birth)} years • {beneficiary.gender || 'N/A'}
                      </div>
                    </div>
                    <div className={`b-status ${beneficiary.is_high_risk ? 'high-risk' : ''}`}>
                      {beneficiary.is_high_risk ? 'High Risk' : beneficiary.health_status || 'Healthy'}
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="info-row">
                      <div className="info-item">
                        <div className="info-label">Category</div>
                        <div className="info-value">{beneficiary.category || 'N/A'}</div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">
                          {beneficiary.category === 'Pregnant Woman' ? 'LMP Date' : 'Birth Date'}
                        </div>
                        <div className="info-value">
                          {formatDate(
                            beneficiary.category === 'Pregnant Woman'
                              ? beneficiary.PregnancyTrackings?.[0]?.lmp_date
                              : beneficiary.date_of_birth
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="info-row">
                      <div className="info-item">
                        <div className="info-label">
                          {beneficiary.category === 'Pregnant Woman'
                            ? 'EDD'
                            : beneficiary.category === 'Child'
                            ? 'Weight'
                            : 'Health Conditions'}
                        </div>
                        <div className="info-value">
                          {beneficiary.category === 'Pregnant Woman'
                            ? formatDate(beneficiary.PregnancyTrackings?.[0]?.edd)
                            : beneficiary.category === 'Child'
                            ? beneficiary.weight || 'N/A'
                            : beneficiary.health_conditions || 'None'}
                        </div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">
                          {beneficiary.category === 'Pregnant Woman'
                            ? 'ANC Visits'
                            : beneficiary.category === 'Child'
                            ? 'Immunization'
                            : 'BP (Last Reading)'}
                        </div>
                        <div className="info-value">
                          {beneficiary.category === 'Pregnant Woman'
                            ? `${ancVisitCount}/4`
                            : beneficiary.category === 'Child'
                            ? beneficiary.ImmunizationRecords?.[0]?.status || 'Up to date'
                            : beneficiary.last_bp_reading || 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className="info-row">
                      <div className="info-item">
                        <div className="info-label">Phone</div>
                        <div className="info-value">{beneficiary.contact_number || 'N/A'}</div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">Last Visit</div>
                        <div className="info-value">
                          {formatDate(beneficiary.HomeVisits?.[0]?.visit_datetime)}
                        </div>
                      </div>
                    </div>
                    <div className="family-tag">
                      <FamilyRestroom className="icon" />
                      {beneficiary.Village?.village_name || 'Unknown Household'} (
                      {beneficiary.family_members || 0} members)
                    </div>
                    <div className="action-row">
                      <div className={`due-tag ${isOverdue(nextVisitDate) ? 'overdue' : ''}`}>
                        <Event className="icon" />
                        {nextVisitDate
                          ? `${
                              beneficiary.category === 'Pregnant Woman' ? 'ANC Visit' : 'Follow-up'
                            } ${isOverdue(nextVisitDate) ? 'Overdue' : 'Due'} (${formatDate(
                              nextVisitDate
                            )})`
                          : 'No Due Services'}
                      </div>
                      <div className="action-buttons">
                        <div className="action-btn" role="button" aria-label="Home action">
                          <Home />
                        </div>
                        <div className="action-btn" role="button" aria-label="Favorite action">
                          <Favorite />
                        </div>
                        <div className="action-btn" role="button" aria-label="More actions">
                          <MoreVert />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <People className="empty-icon" />
              <div className="empty-text">No beneficiaries found matching your criteria</div>
              <button
                className="button"
                onClick={() =>
                  setFilters({ search: '', category: 'all', household: 'all', sort: 'name' })
                }
              >
                <Refresh className="icon" />
                Reset Filters
              </button>
            </div>
          )}
        </div>
      )}

      {viewMode === 'list' && (
        <div className="card-list">
          <div className="list-header">
            <div className="header-cell" style={{ flex: 0.5 }}>
              Name
            </div>
            <div className="header-cell" style={{ flex: 0.5 }}>
              Age / Gender
            </div>
            <div className="header-cell" style={{ flex: 0.5 }}>
              Category
            </div>
            <div className="header-cell" style={{ flex: 0.5 }}>
              Last Visit
            </div>
            <div className="header-cell" style={{ flex: 0.5 }}>
              Due Services
            </div>
            <div className="header-cell" style={{ flex: 0.5 }}>
              Status
            </div>
            <div className="header-cell" style={{ flex: 0.3 }}>
              Actions
            </div>
          </div>
          {beneficiaries.length > 0 ? (
            beneficiaries.map((beneficiary) => {
              const { ancVisitCount, nextVisitDate } = getAncInfo(beneficiary.PregnancyTrackings);
              return (
                <div className="list-row" key={beneficiary.beneficiary_id}>
                  <div className="list-cell name-cell" style={{ flex: 0.5 }}>
                    {beneficiary.name || 'Unknown'}
                  </div>
                  <div className="list-cell" style={{ flex: 0.5 }}>
                    {calculateAge(beneficiary.date_of_birth)} years / {beneficiary.gender || 'N/A'}
                  </div>
                  <div className="list-cell" style={{ flex: 0.5 }}>
                    <div className={`category-chip chip-${beneficiary.category}`}>
                      {beneficiary.category || 'N/A'}
                    </div>
                  </div>
                  <div className="list-cell" style={{ flex: 0.5 }}>
                    {formatDate(beneficiary.HomeVisits?.[0]?.visit_datetime)}
                  </div>
                  <div className="list-cell" style={{ flex: 0.5 }}>
                    <div className={`due-tag ${isOverdue(nextVisitDate) ? 'overdue' : ''}`}>
                      {nextVisitDate
                        ? `${
                            beneficiary.category === 'Pregnant Woman' ? 'ANC' : 'Follow-up'
                          } ${isOverdue(nextVisitDate) ? 'Overdue' : 'Due'} (${formatDate(
                            nextVisitDate
                          )})`
                        : 'No Due Services'}
                    </div>
                  </div>
                  <div className="list-cell" style={{ flex: 0.5 }}>
                    <div className={`b-status ${beneficiary.is_high_risk ? 'high-risk' : ''}`}>
                      {beneficiary.is_high_risk ? 'High Risk' : beneficiary.health_status || 'Healthy'}
                    </div>
                  </div>
                  <div className="list-cell" style={{ flex: 0.3 }}>
                    <div className="action-icons">
                      <div className="action-btn" role="button" aria-label="Home action">
                        <Home />
                      </div>
                      <div className="action-btn" role="button" aria-label="Favorite action">
                        <Favorite />
                      </div>
                      <div className="action-btn" role="button" aria-label="More actions">
                        <MoreVert />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <People className="empty-icon" />
              <div className="empty-text">No beneficiaries found matching your criteria</div>
              <button
                className="button"
                onClick={() =>
                  setFilters({ search: '', category: 'all', household: 'all', sort: 'name' })
                }
              >
                <Refresh className="icon" />
                Reset Filters
              </button>
            </div>
          )}
        </div>
      )}

      <div className="pagination">
        <div
          className="pagination-button"
          onClick={() => handlePageChange(1)}
          disabled={pagination.page === 1}
          role="button"
          aria-label="First page"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handlePageChange(1)}
        >
          <FirstPage />
        </div>
        <div
          className="pagination-button"
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1}
          role="button"
          aria-label="Previous page"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handlePageChange(pagination.page - 1)}
        >
          <ChevronLeft />
        </div>
        {getPaginationButtons().map((page) => (
          <div
            key={page}
            className={`pagination-button ${pagination.page === page ? 'active' : ''}`}
            onClick={() => handlePageChange(page)}
            role="button"
            aria-label={`Page ${page}`}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handlePageChange(page)}
          >
            {page}
          </div>
        ))}
        <div
          className="pagination-button"
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page === pagination.totalPages}
          role="button"
          aria-label="Next page"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handlePageChange(pagination.page + 1)}
        >
          <ChevronRight />
        </div>
        <div
          className="pagination-button"
          onClick={() => handlePageChange(pagination.totalPages)}
          disabled={pagination.page === pagination.totalPages}
          role="button"
          aria-label="Last page"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handlePageChange(pagination.totalPages)}
        >
          <LastPage />
        </div>
      </div>
    </>
  );
};

export default BeneficiariesPage;